// Collection list layout (sections/main-collection-product-list):
// <product-list-view-toggle> lets visitors pick "Photos" or "3D models" as the main image,
// <product-list-model> shows a row's spinning 3D model (main view, or on hover in photo view),
// <product-list-nav> is the floating "Jump to" category menu.

const PRODUCT_LIST_VIEW_EVENT = 'product-list:view-change';
// 3D models are desktop-only on the collection page: same breakpoint as the floating menu/toggle.
const PRODUCT_LIST_3D_MEDIA = '(min-width: 990px)';
const PRODUCT_LIST_VIEW_KEY = 'cfspan:product-list-view';

const getStoredProductListView = () => {
  try {
    return window.localStorage.getItem(PRODUCT_LIST_VIEW_KEY);
  } catch (error) {
    return null;
  }
};

if (!customElements.get('product-list-view-toggle')) {
  customElements.define(
    'product-list-view-toggle',
    class ProductListViewToggle extends HTMLElement {
      connectedCallback() {
        this.container = this.closest('[data-product-view]');
        this.buttons = [...this.querySelectorAll('[data-view]')];
        if (!this.container || !this.buttons.length) return;

        this.onClick = this.onClick.bind(this);
        this.buttons.forEach((button) => button.addEventListener('click', this.onClick));

        const stored = getStoredProductListView();
        this.apply(stored === 'photo' || stored === 'model' ? stored : this.container.dataset.productView);
      }

      disconnectedCallback() {
        this.buttons?.forEach((button) => button.removeEventListener('click', this.onClick));
      }

      onClick(event) {
        const view = event.currentTarget.dataset.view;
        try {
          window.localStorage.setItem(PRODUCT_LIST_VIEW_KEY, view);
        } catch (error) {
          // Storage unavailable (private mode etc.); the choice just won't be remembered.
        }
        this.apply(view);
      }

      apply(view) {
        this.container.dataset.productView = view;
        this.buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.view === view)));
        this.container.dispatchEvent(new CustomEvent(PRODUCT_LIST_VIEW_EVENT, { detail: { view } }));
      }
    }
  );
}

if (!customElements.get('product-list-model')) {
  // Shopify serves <model-viewer> as its own feature. It only autoloads for tags carrying
  // data-shopify-feature, so request it explicitly, once, for every row on the page.
  const MODEL_VIEWER_VERSION = '1.12';

  const loadModelViewer = () => {
    if (!window.productListModelViewer) {
      window.productListModelViewer = new Promise((resolve, reject) => {
        if (customElements.get('model-viewer')) return resolve();
        if (!window.Shopify?.loadFeatures) return reject(new Error('Shopify.loadFeatures unavailable'));

        window.Shopify.loadFeatures([
          {
            name: 'model-viewer',
            version: MODEL_VIEWER_VERSION,
            onLoad: (errors) => (errors ? reject(errors) : resolve()),
          },
        ]);
      }).then(() => customElements.whenDefined('model-viewer'));
    }
    return window.productListModelViewer;
  };

  customElements.define(
    'product-list-model',
    class ProductListModel extends HTMLElement {
      connectedCallback() {
        this.media = this.closest('.product-row__media');
        this.container = this.closest('[data-product-view]');
        if (!this.media) return;

        this.update = this.update.bind(this);
        this.onEnter = this.onEnter.bind(this);
        this.onLeave = this.onLeave.bind(this);

        this.desktop = window.matchMedia(PRODUCT_LIST_3D_MEDIA);
        this.desktop.addEventListener('change', this.update);

        // Hover swaps views only where hovering exists; touch screens just show the chosen view.
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          this.media.addEventListener('pointerenter', this.onEnter);
          this.media.addEventListener('pointerleave', this.onLeave);
        }
        this.container?.addEventListener(PRODUCT_LIST_VIEW_EVENT, this.update);

        // In 3D view, load models as rows come near the viewport.
        this.observer = new IntersectionObserver(
          (entries) => {
            this.nearViewport = entries.some((entry) => entry.isIntersecting);
            this.update();
          },
          { rootMargin: '200px 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.desktop?.removeEventListener('change', this.update);
        this.media?.removeEventListener('pointerenter', this.onEnter);
        this.media?.removeEventListener('pointerleave', this.onLeave);
        this.container?.removeEventListener(PRODUCT_LIST_VIEW_EVENT, this.update);
      }

      get modelView() {
        return this.container?.dataset.productView === 'model';
      }

      onEnter() {
        this.hovering = true;
        this.update();
      }

      onLeave() {
        this.hovering = false;
        this.update();
      }

      // Show the model when it's the chosen view and not hovered, or the photo view and hovered.
      update() {
        // Phones/tablets never get the 3D model on this page (nothing is downloaded either).
        const wantModel = this.desktop.matches && this.modelView !== Boolean(this.hovering);

        if (wantModel && !this.started && (this.hovering || this.nearViewport)) {
          this.load();
        }

        if (wantModel && this.ready) {
          this.media.classList.add('product-row__media--model-active');
          if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            this.viewer.setAttribute('auto-rotate', '');
          }
        } else {
          this.media.classList.remove('product-row__media--model-active');
          this.viewer?.removeAttribute('auto-rotate'); // pause where it is
        }
      }

      async load() {
        this.started = true;
        this.media.classList.add('product-row__media--model-loading');

        try {
          await loadModelViewer();
        } catch (error) {
          // Keep showing the product photo if the viewer can't load.
          console.warn('product-list-model: 3D viewer unavailable', error);
          this.media.classList.remove('product-row__media--model-loading');
          return;
        }

        this.viewer = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
        if (!this.viewer) return;

        this.viewer.addEventListener(
          'load',
          () => {
            this.ready = true;
            this.media.classList.remove('product-row__media--model-loading');
            this.update();
          },
          { once: true }
        );
        this.viewer.addEventListener(
          'error',
          (event) => {
            this.media.classList.remove('product-row__media--model-loading');
            console.warn('product-list-model: model failed', event);
          },
          { once: true }
        );
        this.appendChild(this.viewer);
      }
    }
  );
}

if (!customElements.get('product-list-nav')) {
  customElements.define(
    'product-list-nav',
    class ProductListNav extends HTMLElement {
      connectedCallback() {
        this.links = [...this.querySelectorAll('a[href^="#"]')];
        this.groups = this.links.map((link) => document.getElementById(link.hash.slice(1))).filter(Boolean);
        if (!this.groups.length) return;

        this.onClick = this.onClick.bind(this);
        this.links.forEach((link) => link.addEventListener('click', this.onClick));

        // Highlight the category currently at the top of the viewport.
        this.visible = new Set();
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) =>
              entry.isIntersecting ? this.visible.add(entry.target) : this.visible.delete(entry.target)
            );
            const current = this.groups.find((group) => this.visible.has(group));
            if (current) this.setCurrent(current.id);
          },
          { rootMargin: '-20% 0px -60% 0px' }
        );
        this.groups.forEach((group) => this.observer.observe(group));
        this.setCurrent(this.groups[0].id);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.links?.forEach((link) => link.removeEventListener('click', this.onClick));
      }

      onClick(event) {
        const target = document.getElementById(event.currentTarget.hash.slice(1));
        if (!target) return;

        event.preventDefault();
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        history.replaceState(null, '', event.currentTarget.hash);
        this.setCurrent(target.id);
      }

      setCurrent(id) {
        this.links.forEach((link) => {
          if (link.hash.slice(1) === id) {
            link.setAttribute('aria-current', 'true');
          } else {
            link.removeAttribute('aria-current');
          }
        });
      }
    }
  );
}
